// Plot edits (pure). Site shape: see structureRules.js. Trees: { [entityId]: Node[] }.

import { COMMON_ENTITY, PARKING_ENTITY, isLinked, isSingleBuilding, presetTree, seqNames } from "./structureRules.js";

const clone = (x) => JSON.parse(JSON.stringify(x));
const buildings = (site) => site.ents.filter((e) => e.kind === "bldg");

const nextEntityId = (site) => {
  let i = 1;
  while (site.ents.some((e) => e.id === `e${i}`)) i++;
  return `e${i}`;
};

/** Rename every building in list order from the naming rule. */
export function applyNamingRule(site, rule) {
  const s = clone(site);
  s.naming = { rule };
  const names = seqNames(rule, buildings(s).length).names;
  buildings(s).forEach((b, i) => {
    b.name = names[i];
  });
  return s;
}

export function addBuilding(site) {
  const s = clone(site);
  const count = buildings(s).length + 1;
  const name = seqNames(s.naming?.rule, count).names[count - 1];
  const template = buildings(s).at(-1);
  const building = {
    id: nextEntityId(s),
    kind: "bldg",
    name,
    tpl: "building",
    zones: template?.zones ?? 1,
    down: template?.down ?? 1,
    up: template?.up ?? 9,
    typ: null,
    link: false,
  };
  const lastBuildingIndex = s.ents.map((e) => e.kind).lastIndexOf("bldg");
  s.ents.splice(lastBuildingIndex + 1, 0, building);
  if (count === 2 && !s.ents.some((e) => e.kind === "common") && !s.commonOff) s.ents.unshift({ ...COMMON_ENTITY });
  if (s.sameStructure) linkBuildingsInPlace(s);
  return s;
}

/**
 * Delete an object; the last building cannot go. Buildings linked to it keep its structure:
 * the first of them takes over its tree and the others link to that one.
 * Returns { site, trees }.
 */
export function removeEntity(site, trees, id) {
  const ent = site.ents.find((e) => e.id === id);
  if (!ent || (ent.kind === "bldg" && buildings(site).length === 1)) return { site, trees };
  const s = clone(site);
  const t = clone(trees);
  s.ents = s.ents.filter((e) => e.id !== id);
  const copies = s.ents.filter((e) => e.typ === id);
  if (copies.length) {
    const [heir, ...rest] = copies;
    heir.typ = null;
    heir.link = false;
    t[heir.id] = t[id] || [];
    rest.forEach((e) => {
      e.typ = heir.id;
    });
  }
  delete t[id];
  s.ents.forEach((e) => {
    if (e.serves) e.serves = e.serves.filter((x) => x !== id);
  });
  if (s.sameStructure) linkBuildingsInPlace(s);
  return { site: s, trees: t };
}

/** Move a building up/down among the buildings (changes object numbers). */
export function moveBuilding(site, id, dir) {
  const s = clone(site);
  const order = s.ents.map((e, i) => ({ e, i })).filter((x) => x.e.kind === "bldg");
  const at = order.findIndex((x) => x.e.id === id);
  const to = at + dir;
  if (at < 0 || to < 0 || to >= order.length) return site;
  const [a, b] = [order[at].i, order[to].i];
  [s.ents[a], s.ents[b]] = [s.ents[b], s.ents[a]];
  if (s.naming?.rule) {
    const names = seqNames(s.naming.rule, buildings(s).length).names;
    buildings(s).forEach((bld, i) => {
      bld.name = names[i];
    });
  }
  if (s.sameStructure) linkBuildingsInPlace(s);
  return s;
}

export function updateEntity(site, id, patch) {
  const s = clone(site);
  Object.assign(
    s.ents.find((e) => e.id === id),
    patch
  );
  return s;
}

export function setCommon(site, on) {
  const s = clone(site);
  s.ents = s.ents.filter((e) => e.kind !== "common");
  s.commonOff = !on;
  if (on) s.ents.unshift({ ...COMMON_ENTITY });
  return s;
}

export function setParking(site, on) {
  const s = clone(site);
  s.ents = s.ents.filter((e) => e.kind !== "shared");
  if (on) s.ents.push({ ...PARKING_ENTITY, serves: buildings(s).map((b) => b.id) });
  return s;
}

function linkBuildingsInPlace(s) {
  const [first, ...rest] = buildings(s);
  if (first) {
    first.typ = null;
    first.link = false;
  }
  rest.forEach((b) => {
    b.typ = first.id;
    b.link = true;
  });
}

/**
 * "One structure for all buildings". On: buildings 2… are linked to the first (no tree of their
 * own). Off: each linked building becomes an independent copy of its source's tree.
 */
export function setSameStructure(site, trees, on) {
  const s = clone(site);
  const t = clone(trees);
  s.sameStructure = on;
  if (on) {
    linkBuildingsInPlace(s);
    buildings(s)
      .slice(1)
      .forEach((b) => delete t[b.id]);
  } else {
    buildings(s).forEach((b) => {
      if (b.link && b.typ) t[b.id] = clone(t[b.typ] || []);
      b.typ = null;
      b.link = false;
    });
  }
  return { site: s, trees: t };
}

/**
 * Trees for the objects that need one: missing ones come from the template, trees of removed
 * or linked objects are dropped. `reset`: rebuild every tree (no works yet, building count
 * crossed between one and several, so the presets differ).
 */
export function syncTrees(site, trees, template, { reset = false } = {}) {
  const single = isSingleBuilding(site);
  const out = {};
  site.ents.forEach((e) => {
    if (isLinked(site, e)) return;
    const kind = e.kind === "bldg" ? "building" : e.tpl;
    out[e.id] = !reset && trees[e.id] ? trees[e.id] : presetTree(template, kind, { single });
  });
  return out;
}

/** Short description of an object for the list on the right ("2 ზონა · −1 / 9 სართ."). */
export function describeEntity(site, ent) {
  if (ent.kind === "common") return "ეზო, გარე ქსელები, Soft Cost, OPEX";
  if (ent.kind === "shared") {
    const names = (ent.serves || []).map((id) => site.ents.find((e) => e.id === id)?.name).filter(Boolean);
    return `${ent.down || 1} მიწისქვეშა სართ.${names.length ? ` · ${names.join(", ")}-ის ქვეშ` : ""}`;
  }
  const zones = Number(ent.zones) || 1;
  const linked = isLinked(site, ent) ? ` · = ${site.ents.find((e) => e.id === ent.typ)?.name}` : "";
  return `${zones} ზონა · −${Number(ent.down) || 0} / ${Number(ent.up) || 0} სართ.${linked}`;
}

export const zoneLetters = (count) => Array.from({ length: Math.max(1, Number(count) || 1) }, (_, i) => String.fromCharCode(65 + i));
